import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { fetchWithAuth, authenticatedFetch, API_ENDPOINT } from '../utils/api';
import { Activity, AlertTriangle, ArrowLeft, FileText, Upload, MessageSquare, Users, CheckCircle, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import FileDownloadButton from '../FileDownloadButton';
import { Widget, StatCard, StatusBadge } from './Common';

function EventDetailsView({ eventId, onBack, onUpdate }) {
  const { organization } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [creatingForm, setCreatingForm] = useState(false);
  const [formQuestions, setFormQuestions] = useState([{ id: 'q1', text: 'What aspects of the response worked well?', type: 'long_text', required: true },{ id: 'q2', text: 'What areas need improvement?', type: 'long_text', required: true },{ id: 'q3', text: 'Rate the overall coordination', type: 'rating', required: true },{ id: 'q4', text: 'Additional comments or observations', type: 'long_text', required: false }]);
  const [formStatus, setFormStatus] = useState('published');
  const [copiedFormId, setCopiedFormId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [editFormTitle, setEditFormTitle] = useState('');
  const [editFormDescription, setEditFormDescription] = useState('');
  const [editFormQuestions, setEditFormQuestions] = useState([]);
  const [editFormStatus, setEditFormStatus] = useState('published');
  const [updatingForm, setUpdatingForm] = useState(false);
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [viewingForm, setViewingForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);

  useEffect(() => {
    loadEventDetails();
  }, [eventId]);

  const loadEventDetails = async () => {
  if (!organization?.organizationId) {
    console.error('Organization not loaded');
    setLoading(false);
    return;
  }

  try {
  const data = await fetchWithAuth(`/events/${eventId}`, organization.organizationId);
  setEvent(data);

  } catch (error) {
    console.error('Error loading event:', error);
    alert('Failed to load event details');
  } finally {
    setLoading(false);
  }
};

  const handleGenerateAAR = async () => {
    if (!window.confirm('Generate AAR now? This will analyze all uploaded documents and feedback.')) return;
    
    setGenerating(true);
    try {
      const data = await authenticatedFetch(
        `${API_ENDPOINT}/events/${eventId}/generate-aar?organization_id=${organization?.organizationId}`,
        { method: 'POST' }
      );
      
      toast.success('AAR generation started! This may take a few minutes.', {
        duration: 5000,
        icon: '🚀'
      });
      
      setTimeout(() => {
        loadEventDetails();
        onUpdate();
      }, 2000);
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate AAR: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateAAR = async () => {
    if (!window.confirm('Regenerate AAR? This will create a new version incorporating any new documents or feedback.')) return;
    
    setGenerating(true);
    try {
      const data = await authenticatedFetch(
        `${API_ENDPOINT}/events/${eventId}/regenerate-aar?organization_id=${organization?.organizationId}`,
        { method: 'POST' }
      );
      
      toast.success('AAR regeneration started! Check back in a few minutes.', {
        duration: 5000,
        icon: '🔄'
      });
      
      setTimeout(() => {
        loadEventDetails();
        onUpdate();
      }, 2000);
    } catch (error) {
      console.error('Regeneration error:', error);
      toast.error('Failed to regenerate AAR: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleUploadAdditionalDocs = async () => {
    if (filesToUpload.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    setUploadingFiles(true);
    const uploadToast = toast.loading(`Uploading ${filesToUpload.length} file(s)...`);
    
    try {
      const filePromises = filesToUpload.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              filename: file.name,
              file_type: file.name.split('.').pop().toLowerCase(),
              content: reader.result.split(',')[1]
            });
          };
          reader.readAsDataURL(file);
        });
      });

      const encodedFiles = await Promise.all(filePromises);

      const response = await authenticatedFetch(
        `${API_ENDPOINT}/events/${eventId}/documents?organization_id=${organization?.organizationId}`,
        {
          method: 'POST',
          body: JSON.stringify({ files: encodedFiles })
        }
      );

      if (response?.ok !== false && !response?.error) {
        toast.success(`Successfully uploaded ${filesToUpload.length} document(s)!`, {
          id: uploadToast,
          icon: '✅'
        });
        setShowUploadModal(false);
        setFilesToUpload([]);
        loadEventDetails();
        onUpdate();
      } else {
        toast.error(`Failed to upload: ${response?.error || 'Unknown error'}`, { id: uploadToast });
      }
    } catch (error) {
      toast.error(`Error uploading files: ${error.message}`, { id: uploadToast });
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteDocument = async (filename) => {
  if (!window.confirm(`Delete "${filename}"? This cannot be undone.`)) {
    return;
  }

  const deleteToast = toast.loading('Deleting document...');
  
  try {
    const response = await authenticatedFetch(
      `${API_ENDPOINT}/events/${eventId}/documents/${encodeURIComponent(filename)}?organization_id=${organization?.organizationId}`,
      { method: 'DELETE' }
    );

    if (response?.ok !== false && !response?.error) {
      toast.success('Document deleted successfully!', {
        id: deleteToast,
        icon: '🗑️'
      });
      loadEventDetails();
      onUpdate();
    } else {
      toast.error(`Failed to delete: ${response?.error || 'Unknown error'}`, { id: deleteToast });
    }
  } catch (error) {
    toast.error(`Error deleting document: ${error.message}`, { id: deleteToast });
  }
};

  const handleCreateFeedbackForm = async () => {
    if (!formTitle.trim()) { toast.error('Please enter a form title'); return; }
    if (formQuestions.some(q => !q.text.trim())) { toast.error('Please fill in all question text fields'); return; }

    setCreatingForm(true);
    const createToast = toast.loading('Creating feedback form...');
    
    try {
      const formResponse = await authenticatedFetch(
        `${API_ENDPOINT}/events/feedback-form/create?organization_id=${organization?.organizationId}`,
        {
          method: 'POST',
          body: JSON.stringify({
            event_id: eventId,
            aar_id: eventId,
            organization_id: organization?.organizationId,
            form_title: formTitle,
            form_description: formDescription,
            status: formStatus,
            sections: [{ section_title: 'Event Feedback', questions: formQuestions.map(q => ({ question_id: q.id, question_text: q.text, question_type: q.type, required: q.required })) }]
          })
        }
      );

      // Handle both raw Response and pre-parsed data
      const result = formResponse?.json ? await formResponse.json() : formResponse;

      if (result && (result.form_id || result.message)) {
        if (formStatus === 'published' && result.form_id) {
          const shareableLink = `${window.location.origin}/feedback/${result.form_id}`;
          toast.success(`Feedback form created and published!\n\nLink copied to clipboard!`, { id: createToast, duration: 6000, icon: '🎉' });
          navigator.clipboard.writeText(shareableLink);
        } else {
          toast.success('Feedback form saved as draft!', { id: createToast, icon: '📝' });
        }
        setShowFeedbackModal(false);
        setFormTitle('');
        setFormDescription('');
        setFormStatus('published');
        setFormQuestions([
          { id: 'q1', text: 'What aspects of the response worked well?', type: 'long_text', required: true },
          { id: 'q2', text: 'What areas need improvement?', type: 'long_text', required: true },
          { id: 'q3', text: 'Rate the overall coordination', type: 'rating', required: true },
          { id: 'q4', text: 'Additional comments or observations', type: 'long_text', required: false }
        ]);
        setTimeout(() => { loadEventDetails(); }, 1000);
      } else {
        toast.error(`Failed to create form: ${result?.error || 'Unknown error'}`, { id: createToast });
      }
    } catch (error) {
      toast.error(`Error creating form: ${error.message}`, { id: createToast });
    } finally {
      setCreatingForm(false);
    }
  };

  const handleEditForm = (form) => {
    setEditingForm(form);
    setEditFormTitle(form.form_title);
    setEditFormDescription(form.form_description || '');
    setEditFormStatus(form.status);
    const questions = form.sections?.[0]?.questions || [];
    setEditFormQuestions(questions.map(q => ({ id: q.question_id, text: q.question_text, type: q.question_type, required: q.required })));
    setShowEditModal(true);
  };

  const handleUpdateFeedbackForm = async () => {
    if (!editFormTitle.trim()) { toast.error('Please enter a form title'); return; }
    if (editFormQuestions.some(q => !q.text.trim())) { toast.error('Please fill in all question text fields'); return; }

    setUpdatingForm(true);
    const updateToast = toast.loading('Updating form...');
    
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/events/feedback-form/${editingForm.form_id}/update?organization_id=${organization?.organizationId}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            form_title: editFormTitle,
            form_description: editFormDescription,
            status: editFormStatus,
            sections: [{ section_title: 'Event Feedback', questions: editFormQuestions.map(q => ({ question_id: q.id, question_text: q.text, question_type: q.type, required: q.required })) }]
          })
        }
      );

      if (response?.ok !== false && !response?.error) {
        toast.success('Form updated successfully!', { id: updateToast, icon: '✅' });
        setShowEditModal(false);
        setEditingForm(null);
        loadEventDetails();
      } else {
        toast.error(`Failed to update form: ${response?.error || 'Unknown error'}`, { id: updateToast });
      }
    } catch (error) {
      toast.error(`Error updating form: ${error.message}`, { id: updateToast });
    } finally {
      setUpdatingForm(false);
    }
  };

  const handleViewResponses = async (form) => {
    setViewingForm(form);
    setShowResponsesModal(true);
    setLoadingResponses(true);
    try {
      const data = await fetchWithAuth(`/events/feedback-form/${form.form_id}/responses`, organization?.organizationId);
      setResponses(data.responses || []);
    } catch (error) {
      console.error('Error loading responses:', error);
      toast.error('Error loading responses');
      setResponses([]);
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleExportToCSV = () => {
    if (responses.length === 0) { toast.error('No responses to export'); return; }
    try {
    const questions = viewingForm.sections?.[0]?.questions || [];
    const headers = ['Response #','Submitted At','Respondent Name','Respondent Role','Respondent Email',...questions.map(q => q.question_text)];
    const rows = responses.map((response, index) => {
      return [
        responses.length - index,
        new Date(response.submitted_at).toLocaleString(),
        response.respondent_name || 'Anonymous',
        response.respondent_role || '',
        response.respondent_email || '',
        ...questions.map(q => {
          const answer = response.responses[q.question_id];
          if (q.question_type === 'rating') return answer || 'N/A';
          else if (q.question_type === 'yes_no') return answer ? answer.toUpperCase() : 'N/A';
          else {
            const value = answer || '';
            if (value.includes(',') || value.includes('\n') || value.includes('"')) return `"${value.replace(/"/g, '""')}"`;
            return value;
          }
        })
      ];
    });
    const csvContent = [headers.map(h => `"${h}"`).join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${viewingForm.form_title.replace(/[^a-z0-9]/gi, '_')}_responses_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV exported successfully!', { icon: '📊' });
  } catch (error) {
    console.error('Error exporting CSV:', error);
    alert('Failed to export CSV: ' + error.message);
  }
};

  if (loading) {
    return (<div className="flex items-center justify-center py-12"><Activity className="w-8 h-8 text-blue-500 animate-spin mr-3" /><p className="text-gray-600">Loading event details...</p></div>);
  }

  if (!event) {
    return (<div className="text-center py-12"><AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">Event not found</p><button onClick={onBack} className="mt-4 text-blue-600 hover:text-blue-800">Back to Events</button></div>);
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={onBack} className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium">
        <ArrowLeft className="w-5 h-5" /><span>Back to All Events</span>
      </button>

      {/* Event Details - Custom Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Event Details</h2>
          <StatusBadge status={event.status} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-5">{event.incident_name}</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date</p>
                  <p className="text-sm font-semibold text-gray-900">{new Date(event.incident_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Incident Type</p>
                  <p className="text-sm font-semibold text-gray-900 capitalize">{event.incident_type?.replace(/_/g, ' ')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hazard Type</p>
                  <p className="text-sm font-semibold text-gray-900 capitalize">{event.hazard_type?.replace(/_/g, ' ')}</p>
                </div>
              </div>
              {event.created_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created</p>
                    <p className="text-sm font-semibold text-gray-900">{new Date(event.created_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              )}
              {event.created_by && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created By</p>
                    <p className="text-sm font-semibold text-gray-900">{event.created_by}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <StatCard title="Documents" value={event.file_count} icon={<FileText className="w-5 h-5" />} color="blue" />
            <StatCard title="Feedback Forms" value={event.feedback_forms_count} icon={<MessageSquare className="w-5 h-5" />} color="green" />
            <StatCard title="Responses" value={event.total_feedback_responses} icon={<Users className="w-5 h-5" />} color="purple" />
            {event && event.can_add_documents && (
              <button onClick={() => setShowUploadModal(true)} className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                <Upload className="w-5 h-5" /><span>Upload More Documents</span>
              </button>
            )}
            {event.can_create_form && (
              <button onClick={() => setShowFeedbackModal(true)} className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
                <MessageSquare className="w-5 h-5" /><span>Create Feedback Form</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Uploaded Documents */}
      {event.uploaded_files && event.uploaded_files.length > 0 && (
        <Widget title={`Uploaded Documents (${event.file_count})`}>
          <div className="space-y-2">
            {event.uploaded_files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                <div className="flex items-center space-x-3 flex-1">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{file.filename || file}</p>
                    <p className="text-xs text-gray-500">{file.file_type ? file.file_type.toUpperCase() : 'Document'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-3">
                  <FileDownloadButton s3Key={file.s3_key || `${organization.organizationId}/events/${eventId}/${file.filename || file}`} filename={file.filename || file} className="text-xs" />
                  <button onClick={() => handleDeleteDocument(file.filename || file)} className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </Widget>
      )}

      {/* Actions & Generated AAR */}
      <Widget title="Actions & Reports">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {event.can_generate_aar && (
            <button onClick={handleGenerateAAR} disabled={generating} className="flex items-center justify-center space-x-2 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
              <FileText className="w-5 h-5" /><span>{generating ? 'Generating...' : 'Generate AAR'}</span>
            </button>
          )}
          {event.can_regenerate_aar && (
            <button onClick={handleRegenerateAAR} disabled={generating} className="flex items-center justify-center space-x-2 px-6 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50">
              <RefreshCw className={`w-5 h-5 ${generating ? 'animate-spin' : ''}`} /><span>{generating ? 'Regenerating...' : 'Regenerate AAR'}</span>
            </button>
          )}
        </div>

        {event.has_report && event.report && (
          <div className="border-t pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Generated Report</h4>
                <div className="space-y-3 text-sm text-gray-600">
                  <div><span className="font-medium text-gray-700">Generated:</span><p className="text-gray-900">{new Date(event.report_generated_date).toLocaleString()}</p></div>
                  {event.regeneration_count > 0 && (<div><span className="font-medium text-gray-700">Regenerated:</span><p className="text-gray-900">{event.regeneration_count} time(s)</p></div>)}
                  {event.report.includes_feedback && (<p className="text-sm text-green-600 flex items-center space-x-1"><CheckCircle className="w-4 h-4" /><span>Includes {event.report.feedback_response_count} feedback responses</span></p>)}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Download Report</h4>
                {(event.pdf_s3_key || event.docx_s3_key) ? (
                  <div className="grid grid-cols-2 gap-3">
                    {event.pdf_s3_key && (
                      <button onClick={async () => { const downloadToast = toast.loading('Preparing PDF download...'); try { const data = await fetchWithAuth(`/events/${eventId}/download?format=pdf`, organization?.organizationId); toast.success('Download started!', { id: downloadToast, icon: '📄', duration: 2000 }); window.open(data.download_url, '_blank'); } catch (error) { console.error('Error downloading PDF:', error); toast.error('Failed to download PDF', { id: downloadToast }); } }} className="flex flex-col items-center justify-center p-4 bg-red-50 border-2 border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition group">
                        <FileText className="w-8 h-8 text-red-600 mb-2" /><span className="font-medium text-red-700 text-sm">PDF</span><span className="text-xs text-red-600 group-hover:underline mt-1">Download</span>
                      </button>
                    )}
                    {event.docx_s3_key && (
                      <button onClick={async () => { const downloadToast = toast.loading('Preparing Word download...'); try { const data = await fetchWithAuth(`/events/${eventId}/download?format=docx`, organization?.organizationId); toast.success('Download started!', { id: downloadToast, icon: '📝', duration: 2000 }); window.open(data.download_url, '_blank'); } catch (error) { console.error('Error downloading Word doc:', error); toast.error('Failed to download Word document', { id: downloadToast }); } }} className="flex flex-col items-center justify-center p-4 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition group">
                        <FileText className="w-8 h-8 text-blue-600 mb-2" /><span className="font-medium text-blue-700 text-sm">Word</span><span className="text-xs text-blue-600 group-hover:underline mt-1">Download</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                    <div className="flex items-start"><AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" /><p className="text-sm text-yellow-800">Report generated but download files are not yet available. Try regenerating the AAR.</p></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!event.has_report && event.status === 'completed' && (
          <div className="text-center py-8 border-t">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600 mb-4">No AAR report available yet</p>
            {event.can_generate_aar && (<button onClick={handleGenerateAAR} disabled={generating} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50">{generating ? 'Generating...' : 'Generate AAR Now'}</button>)}
          </div>
        )}
      </Widget>

      {/* Feedback Forms */}
      {event.feedback_forms_count > 0 && (
        <Widget title={`Feedback Forms (${event.feedback_forms_count})`}>
          <div className="space-y-3">
            {event.feedback_forms.map(form => (
              <div key={form.form_id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-medium text-gray-900">{form.form_title}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${form.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{form.status === 'published' ? 'Published' : 'Draft'}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{form.response_count || 0} responses • Created {new Date(form.created_at).toLocaleDateString()}</p>
                    {form.status === 'published' && (
                      <div className="flex items-center space-x-2 mb-3">
                        <input type="text" value={`${window.location.origin}/feedback/${form.form_id}`} readOnly className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
                        <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/feedback/${form.form_id}`); setCopiedFormId(form.form_id); setTimeout(() => setCopiedFormId(null), 2000); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium whitespace-nowrap">{copiedFormId === form.form_id ? '✓ Copied!' : 'Copy Link'}</button>
                      </div>
                    )}
                    {form.status === 'draft' && (<p className="text-sm text-amber-600 mb-3">ⓘ This form is in draft mode. Publish it to share with participants.</p>)}
                  </div>
                </div>
                <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-gray-200">
                  {form.status === 'draft' ? (
                    <button onClick={async () => { if (window.confirm('Publish this form? Participants will be able to access it via the link.')) { try { const response = await authenticatedFetch(`${API_ENDPOINT}/events/feedback-form/${form.form_id}/publish?organization_id=${organization?.organizationId}`, { method: 'POST' }); if (response?.ok !== false && !response?.error) { toast.success('Form published successfully!'); loadEventDetails(); } else { toast.error('Failed to publish form'); } } catch (error) { toast.error('Failed to publish form'); } } }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm">Publish</button>
                  ) : (
                    <button onClick={async () => { if (window.confirm('Unpublish this form? The link will no longer be accessible.')) { try { const response = await authenticatedFetch(`${API_ENDPOINT}/events/feedback-form/${form.form_id}/unpublish?organization_id=${organization?.organizationId}`, { method: 'POST' }); if (response?.ok !== false && !response?.error) { toast.success('Form unpublished successfully!'); loadEventDetails(); } else { toast.error('Failed to unpublish form'); } } catch (error) { toast.error('Failed to unpublish form'); } } }} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition text-sm">Unpublish</button>
                  )}
                  <button onClick={() => handleEditForm(form)} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm">Edit</button>
                  <button onClick={() => handleViewResponses(form)} disabled={form.response_count === 0} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed">View Responses ({form.response_count || 0})</button>
                  <button onClick={async () => { if (form.response_count > 0) { if (!window.confirm(`This form has ${form.response_count} response(s). Deleting will permanently remove the form and all responses. Are you sure?`)) return; } else { if (!window.confirm(`Delete "${form.form_title}"? This cannot be undone.`)) return; } try { const response = await authenticatedFetch(`${API_ENDPOINT}/events/feedback-form/${form.form_id}?organization_id=${organization?.organizationId}`, { method: 'DELETE' }); if (response?.ok !== false && !response?.error) { toast.success('Form deleted successfully!'); loadEventDetails(); } else { toast.error(`Failed to delete form: ${response?.error || 'Unknown error'}`); } } catch (error) { toast.error(`Error deleting form: ${error.message}`); } }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </Widget>
      )}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Create Feedback Form</h3>
                <button onClick={() => { setShowFeedbackModal(false); setFormTitle(''); setFormDescription(''); setFormQuestions([{ id: 'q1', text: 'What aspects of the response worked well?', type: 'long_text', required: true },{ id: 'q2', text: 'What areas need improvement?', type: 'long_text', required: true },{ id: 'q3', text: 'Rate the overall coordination', type: 'rating', required: true },{ id: 'q4', text: 'Additional comments or observations', type: 'long_text', required: false }]); }} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
              </div>
              <div className="space-y-6 mb-6">
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Form Title *</label><input type="text" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} placeholder={`${event.incident_name} - Participant Feedback`} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label><textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="Provide instructions for participants filling out this form..." rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div>
                <div>
                  <div className="flex items-center justify-between mb-3"><label className="block text-sm font-medium text-gray-700">Form Questions</label><button onClick={() => { setFormQuestions([...formQuestions, { id: `q${formQuestions.length + 1}`, text: '', type: 'long_text', required: false }]); }} className="text-sm text-blue-600 hover:text-blue-800 font-medium">+ Add Question</button></div>
                  <div className="space-y-4">
                    {formQuestions.map((question, index) => (
                      <div key={question.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start justify-between mb-3"><span className="text-sm font-medium text-gray-600">Question {index + 1}</span>{formQuestions.length > 1 && (<button onClick={() => { setFormQuestions(formQuestions.filter((_, i) => i !== index)); }} className="text-red-600 hover:text-red-800 text-sm">Remove</button>)}</div>
                        <div className="space-y-3">
                          <input type="text" value={question.text} onChange={(e) => { const updated = [...formQuestions]; updated[index].text = e.target.value; setFormQuestions(updated); }} placeholder="Enter your question..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                          <div className="flex items-center space-x-4">
                            <select value={question.type} onChange={(e) => { const updated = [...formQuestions]; updated[index].type = e.target.value; setFormQuestions(updated); }} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"><option value="short_text">Short Text</option><option value="long_text">Long Text</option><option value="rating">Rating (1-5)</option><option value="yes_no">Yes/No</option><option value="multiple_choice">Multiple Choice</option></select>
                            <label className="flex items-center space-x-2 text-sm"><input type="checkbox" checked={question.required} onChange={(e) => { const updated = [...formQuestions]; updated[index].required = e.target.checked; setFormQuestions(updated); }} className="rounded text-blue-600" /><span className="text-gray-700">Required</span></label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <label className="block text-sm font-medium text-blue-900 mb-2">Form Status</label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2"><input type="radio" name="formStatus" value="draft" checked={formStatus === 'draft'} onChange={() => setFormStatus('draft')} className="text-blue-600" /><span className="text-sm text-blue-800">Save as Draft</span></label>
                    <label className="flex items-center space-x-2"><input type="radio" name="formStatus" value="published" checked={formStatus === 'published'} onChange={() => setFormStatus('published')} className="text-blue-600" /><span className="text-sm text-blue-800">Publish Immediately</span></label>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">{formStatus === 'draft' ? "Draft forms can be edited before publishing. The link won't be active yet." : 'Published forms are immediately available via the shareable link.'}</p>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <button onClick={() => { setShowFeedbackModal(false); setFormTitle(''); setFormDescription(''); setFormQuestions([{ id: 'q1', text: 'What aspects of the response worked well?', type: 'long_text', required: true },{ id: 'q2', text: 'What areas need improvement?', type: 'long_text', required: true },{ id: 'q3', text: 'Rate the overall coordination', type: 'rating', required: true },{ id: 'q4', text: 'Additional comments or observations', type: 'long_text', required: false }]); }} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">Cancel</button>
                <button onClick={handleCreateFeedbackForm} disabled={creatingForm || !formTitle.trim() || formQuestions.some(q => !q.text.trim())} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed">{creatingForm ? 'Creating...' : (formStatus === 'draft' ? 'Save as Draft' : 'Create & Publish')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit Feedback Form Modal */}
      {showEditModal && editingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6"><h3 className="text-2xl font-bold text-gray-900">Edit Feedback Form</h3><button onClick={() => { setShowEditModal(false); setEditingForm(null); }} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button></div>
              <div className="space-y-6 mb-6">
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Form Title *</label><input type="text" value={editFormTitle} onChange={(e) => setEditFormTitle(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label><textarea value={editFormDescription} onChange={(e) => setEditFormDescription(e.target.value)} rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div>
                <div>
                  <div className="flex items-center justify-between mb-3"><label className="block text-sm font-medium text-gray-700">Form Questions</label><button onClick={() => { setEditFormQuestions([...editFormQuestions, { id: `q${editFormQuestions.length + 1}`, text: '', type: 'long_text', required: false }]); }} className="text-sm text-blue-600 hover:text-blue-800 font-medium">+ Add Question</button></div>
                  <div className="space-y-4">
                    {editFormQuestions.map((question, index) => (
                      <div key={question.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start justify-between mb-3"><span className="text-sm font-medium text-gray-600">Question {index + 1}</span>{editFormQuestions.length > 1 && (<button onClick={() => { setEditFormQuestions(editFormQuestions.filter((_, i) => i !== index)); }} className="text-red-600 hover:text-red-800 text-sm">Remove</button>)}</div>
                        <div className="space-y-3">
                          <input type="text" value={question.text} onChange={(e) => { const updated = [...editFormQuestions]; updated[index].text = e.target.value; setEditFormQuestions(updated); }} placeholder="Enter your question..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                          <div className="flex items-center space-x-4">
                            <select value={question.type} onChange={(e) => { const updated = [...editFormQuestions]; updated[index].type = e.target.value; setEditFormQuestions(updated); }} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"><option value="short_text">Short Text</option><option value="long_text">Long Text</option><option value="rating">Rating (1-5)</option><option value="yes_no">Yes/No</option><option value="multiple_choice">Multiple Choice</option></select>
                            <label className="flex items-center space-x-2 text-sm"><input type="checkbox" checked={question.required} onChange={(e) => { const updated = [...editFormQuestions]; updated[index].required = e.target.checked; setEditFormQuestions(updated); }} className="rounded text-blue-600" /><span className="text-gray-700">Required</span></label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <label className="block text-sm font-medium text-blue-900 mb-2">Form Status</label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2"><input type="radio" name="editFormStatus" value="draft" checked={editFormStatus === 'draft'} onChange={() => setEditFormStatus('draft')} className="text-blue-600" /><span className="text-sm text-blue-800">Draft</span></label>
                    <label className="flex items-center space-x-2"><input type="radio" name="editFormStatus" value="published" checked={editFormStatus === 'published'} onChange={() => setEditFormStatus('published')} className="text-blue-600" /><span className="text-sm text-blue-800">Published</span></label>
                  </div>
                </div>
                {editingForm.response_count > 0 && (<div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded"><p className="text-sm text-amber-800">⚠️ <strong>Warning:</strong> This form has {editingForm.response_count} existing response(s). Changing questions may affect data consistency.</p></div>)}
              </div>
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <button onClick={() => { setShowEditModal(false); setEditingForm(null); }} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">Cancel</button>
                <button onClick={handleUpdateFeedbackForm} disabled={updatingForm || !editFormTitle.trim() || editFormQuestions.some(q => !q.text.trim())} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed">{updatingForm ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Responses Viewer Modal */}
      {showResponsesModal && viewingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div><h3 className="text-2xl font-bold text-gray-900">{viewingForm.form_title}</h3><p className="text-sm text-gray-600 mt-1">{responses.length} {responses.length === 1 ? 'response' : 'responses'}</p></div>
                <button onClick={() => { setShowResponsesModal(false); setViewingForm(null); setResponses([]); }} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
              </div>
              {loadingResponses && (<div className="flex items-center justify-center py-12"><Activity className="w-8 h-8 text-blue-500 animate-spin mr-3" /><p className="text-gray-600">Loading responses...</p></div>)}
              {!loadingResponses && responses.length === 0 && (<div className="text-center py-12"><MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">No responses yet</p><p className="text-sm text-gray-500 mt-2">Share the form link to start collecting feedback</p></div>)}
              {!loadingResponses && responses.length > 0 && (
                <div className="space-y-6">
                  {viewingForm.sections?.[0]?.questions.some(q => q.question_type === 'rating') && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-6">
                      <h4 className="font-semibold text-blue-900 mb-3">Average Ratings</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {viewingForm.sections[0].questions.filter(q => q.question_type === 'rating').map(question => {
                          const ratingResponses = responses.map(r => r.responses[question.question_id]).filter(val => val !== undefined && val !== null);
                          const average = ratingResponses.length > 0 ? (ratingResponses.reduce((sum, val) => sum + Number(val), 0) / ratingResponses.length).toFixed(1) : 'N/A';
                          return (<div key={question.question_id} className="bg-white p-3 rounded"><p className="text-sm text-gray-700 mb-1">{question.question_text}</p><p className="text-2xl font-bold text-blue-600">{average} {average !== 'N/A' && <span className="text-sm text-gray-500">/ 5</span>}</p></div>);
                        })}
                      </div>
                    </div>
                  )}
                  {responses.map((response, index) => (
                    <div key={response.response_id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-300">
                        <div><h4 className="font-semibold text-gray-900">Response #{responses.length - index}</h4><p className="text-sm text-gray-600 mt-1">{response.respondent_name || 'Anonymous'}{response.respondent_role && ` • ${response.respondent_role}`}</p>{response.respondent_email && (<p className="text-sm text-gray-500">{response.respondent_email}</p>)}</div>
                        <div className="text-right text-sm text-gray-500">{new Date(response.submitted_at).toLocaleString()}</div>
                      </div>
                      <div className="space-y-4">
                        {viewingForm.sections?.[0]?.questions.map((question, qIndex) => {
                          const answer = response.responses[question.question_id];
                          return (
                            <div key={question.question_id} className="bg-white p-4 rounded">
                              <p className="text-sm font-medium text-gray-700 mb-2">{qIndex + 1}. {question.question_text}</p>
                              {question.question_type === 'rating' && (<div className="flex items-center space-x-1">{[1, 2, 3, 4, 5].map(star => (<span key={star} className={`text-2xl ${star <= (answer || 0) ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>))}<span className="ml-2 text-gray-600">({answer || 'No rating'})</span></div>)}
                              {question.question_type === 'yes_no' && (<p className={`font-medium ${answer === 'yes' ? 'text-green-600' : 'text-red-600'}`}>{answer ? answer.toUpperCase() : 'No answer'}</p>)}
                              {(question.question_type === 'short_text' || question.question_type === 'long_text') && (<p className="text-gray-900 whitespace-pre-wrap">{answer || <span className="text-gray-400 italic">No answer provided</span>}</p>)}
                              {question.question_type === 'multiple_choice' && (<p className="text-gray-900">{answer || <span className="text-gray-400 italic">No answer provided</span>}</p>)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!loadingResponses && responses.length > 0 && (
                <div className="mt-6 pt-6 border-t flex justify-between items-center">
                  <p className="text-sm text-gray-600">{responses.length} total {responses.length === 1 ? 'response' : 'responses'}</p>
                  <button onClick={handleExportToCSV} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">Export to CSV</button>
                </div>
              )}    
            </div>
          </div>
        </div>
      )}

      {/* Upload Additional Documents Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6"><h3 className="text-2xl font-bold text-gray-900">Upload Additional Documents</h3><button onClick={() => { setShowUploadModal(false); setFilesToUpload([]); }} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button></div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Files</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600"><label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"><span>Upload files</span><input type="file" multiple onChange={(e) => setFilesToUpload(Array.from(e.target.files))} className="sr-only" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.mp3,.wav,.mp4,.m4a" /></label><p className="pl-1">or drag and drop</p></div>
                  <p className="text-xs text-gray-500">PDF, Word, Excel, Images, Audio files</p>
                </div>
              </div>
              {filesToUpload.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700">Selected files:</p>
                  {filesToUpload.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center space-x-2"><FileText className="w-4 h-4 text-gray-400" /><span className="text-sm">{file.name}</span><span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span></div>
                      <button onClick={() => { setFilesToUpload(filesToUpload.filter((_, i) => i !== index)); }} className="text-red-600 hover:text-red-800 text-sm">Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-6"><p className="text-sm text-blue-800"><strong>Note:</strong> These documents will be added to the event. If you have already generated an AAR, consider regenerating it to include analysis from these new documents.</p></div>
            <div className="flex items-center justify-end space-x-3">
              <button onClick={() => { setShowUploadModal(false); setFilesToUpload([]); }} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">Cancel</button>
              <button onClick={handleUploadAdditionalDocs} disabled={uploadingFiles || filesToUpload.length === 0} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed">{uploadingFiles ? 'Uploading...' : `Upload ${filesToUpload.length} File(s)`}</button>
            </div>
          </div>
        </div>
      )}  
    </div>
  );
}

export default EventDetailsView;